import { defineStore } from 'pinia'
import { Feature } from '../enums/feature'
import { useBrowser } from '../utils/browser'
import { useCursorStore } from './cursor'
import { useFeatureStore } from './feature'
import { useInactivityTimerStore } from './inactivityTimer'
import { useMenuStore } from './menu'
import { useUserStore } from './user'
import { useVdAgentStore } from './vdAgent'

interface State {
    ready: boolean
    lastInboundReport: Record<string, any>
    mobile: boolean
    loadingState: number
    focused: boolean
    connectionRetry: number
    readonly: boolean
    webcam: boolean
    capture: boolean
    kbps: number
    streamConnection: any | null
    webcamConnection: any | null
    webcamVideoStream: any | null
    webcamAudioStream: any | null
    stunturn: any | null
    videoDevices: any[]
    audioDevices: any[]
    disconnectedWithoutError: boolean
    displayPlayerWeytopAdvices: boolean
}

export const useStore = defineStore('main', {
    state: (): State => ({
        ready: false,
        lastInboundReport: {},
        mobile: false,
        loadingState: 0,
        focused: false,
        connectionRetry: 0,
        readonly: false,
        webcam: false,
        capture: false,
        kbps: 0,
        streamConnection: null,
        webcamConnection: null,
        webcamVideoStream: null,
        webcamAudioStream: null,
        stunturn: null,
        videoDevices: [],
        audioDevices: [],
        disconnectedWithoutError: false,
        displayPlayerWeytopAdvices: true,
    }),

    actions: {
        enableCapture(): void {
            const menuStore = useMenuStore()

            if (menuStore.isSideMenuDisplayed || this.readonly) {
                return
            }

            this.capture = true
        },

        disableCapture(): void {
            this.capture = false

            if (!this.readonly) {
                const cursorStore = useCursorStore()

                cursorStore.completePendingLocalClickDownEvent()
            }
        },

        updateLockState(): void {
            const userStore = useUserStore()
            const menuStore = useMenuStore()
            const browser = useBrowser()

            if (this.readonly || !this.capture) {
                browser.unlockPointer()
                browser.unlockKeyboard()

                return
            }

            if (menuStore.isSideMenuDisplayed) {
                browser.unlockPointer()
                browser.unlockKeyboard()

                return
            }

            if (
                userStore.user.player_preferences
                    .is_cursor_captured_mode_enabled
            ) {
                browser.lockPointer()
                browser.lockKeyboard()

                return
            }

            browser.unlockPointer()
            browser.lockKeyboard()
        },

        closeVideo(): void {
            const videoElement = document.getElementById(
                'video'
            ) as HTMLVideoElement

            if (videoElement) {
                videoElement.style.display = 'none'
            }
        },

        canPlayVideo(): void {
            setTimeout(() => {
                this.ready = true
            }, 300)
        },

        enableReadOnly(): void {
            const inactivityTimerStore = useInactivityTimerStore()

            this.readonly = true

            inactivityTimerStore.stopTimers()

            this.disableCapture()
            this.updateLockState()
        },

        async disableReadOnly(): Promise<void> {
            const cursorStore = useCursorStore()
            const inactivityTimerStore = useInactivityTimerStore()
            const vdAgentStore = useVdAgentStore()

            this.readonly = false

            inactivityTimerStore.startTimers()

            await cursorStore.initRemoteCursorCaptureMode()

            if (
                vdAgentStore.isEnabled
                && localStorage.getItem('fullscreen') === 'true'
            ) {
                const browser = useBrowser()

                browser.enterFullscreen()
            }

            this.enableCapture()
            this.updateLockState()
        },

        exitPlayer(): void {
            const featureStore = useFeatureStore()

            if (window.shared.web_player.player_custom_app_return_url) {
                window.location.href =
                    window.shared.web_player.player_custom_app_return_url

                return
            }

            if (featureStore.isDisabled(Feature.USER_INTERFACE)) {
                fetch('/logout', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'X-CSRF-TOKEN':
                            document
                                .querySelector('meta[name="csrf-token"]')
                                ?.getAttribute('content') || '',
                    },
                })
                    .then((response) => {
                        if (response.ok) {
                            window.location.href = '/'
                        }
                    })
                    .catch((error) => {
                        console.error(error)
                    })

                return
            }

            window.location.href = '/'
        },
    },
})
